import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { login, clearAuthError } from '@/features/auth/authSlice';
import luciLogo from '@/assets/images/luci_logo_min.png';
import styles from './LoginPage.module.css';

const FEATURES = [
  { icon: '⚡', label: 'AI-powered search', desc: 'Find any moment across your entire media library instantly' },
  { icon: '🎬', label: 'Content Understanding', desc: 'Auto-transcription, scene detection, and speaker identification' },
  { icon: '🔒', label: 'Enterprise-grade security', desc: 'Role-based access with full audit trail' },
];

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useAppSelector((s) => s.auth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !password) return;
    dispatch(login({ email, password }));
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (error) dispatch(clearAuthError());
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (error) dispatch(clearAuthError());
    setPassword(e.target.value);
  };

  return (
    <div className={styles.root}>
      {/* ── Left — form panel ── */}
      <div className={styles.formPanel}>
        <div className={styles.formInner}>
          {/* Logo */}
          <div className={styles.logoRow}>
            <img src={luciLogo} alt="Lucihub" className={styles.logoMark} />
            <div className={styles.logoText}>
              Lucihub <span>Smart Hub</span>
            </div>
          </div>

          {/* Heading */}
          <div className={styles.heading}>
            <h1 className={styles.title}>Sign in</h1>
            <p className={styles.subtitle}>Enter your credentials to access Smart Hub</p>
          </div>

          {/* Form */}
          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <div className={styles.field}>
              <label htmlFor="email" className={styles.label}>Email address</label>
              <input
                id="email"
                type="email"
                className={`${styles.input} ${error ? styles.inputError : ''}`}
                placeholder="name@company.com"
                value={email}
                onChange={handleEmailChange}
                autoComplete="email"
                required
                autoFocus
              />
            </div>

            <div className={styles.field}>
              <div className={styles.labelRow}>
                <label htmlFor="password" className={styles.label}>Password</label>
                <button type="button" className={styles.forgotLink}>Forgot password?</button>
              </div>
              <div className={styles.inputWrap}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className={`${styles.input} ${styles.inputWithToggle} ${error ? styles.inputError : ''}`}
                  placeholder="Enter your password"
                  value={password}
                  onChange={handlePasswordChange}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className={styles.eyeToggle}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className={styles.errorMsg} role="alert">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {error}
              </div>
            )}

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={loading || !email || !password}
            >
              {loading && <span className={styles.spinner} aria-hidden />}
              {loading ? 'Signing in…' : 'Continue'}
            </button>
          </form>

          <p className={styles.formFooter}>
            © 2026 Lucihub · Smart Hub Platform
          </p>
        </div>
      </div>

      {/* ── Right — brand panel ── */}
      <div className={styles.brandPanel} aria-hidden>
        <div className={styles.brandContent}>
          <div className={styles.brandBadge}>BeeHive DAM · v2</div>
          <h2 className={styles.brandTitle}>Your entire media&nbsp;library,&nbsp;intelligently indexed</h2>
          <p className={styles.brandDesc}>
            Smart Hub gives your team instant, AI-powered access to every video, podcast, and document — searchable by content, not just filename.
          </p>

          <ul className={styles.featureList}>
            {FEATURES.map((f) => (
              <li key={f.label} className={styles.featureItem}>
                <span className={styles.featureIcon}>{f.icon}</span>
                <div>
                  <p className={styles.featureLabel}>{f.label}</p>
                  <p className={styles.featureDesc}>{f.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Subtle grid pattern overlay */}
        <div className={styles.gridOverlay} />
      </div>
    </div>
  );
}
